import React, { useState, useEffect } from 'react';
import {
  Drawer,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { type OfferStandalonePackageDTO } from '../../services/offerStandaloneManageService';
import contractorService, { Contractor } from '../../services/contractorService';
import { searchGoogleContacts, GoogleContactSuggestion } from '../../services/contactsService';

// Interfejs dla lokalnego formatu zapytania (kompatybilny z UI)
interface InquiryUI {
  id: string;
  packageId: string;
  packageName: string;
  contractorEmail: string;
  contractorPhone1?: string;
  contractorPhone2?: string;
  subject: string;
  status: 'Przygotowane' | 'draft gmail' | 'Wysłane' | 'Odpowiedź' | 'Odmówiono';
  createdAt: string;
  inviteToCollectifileBoard?: boolean;
}

interface AddInquiryFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (inquiryData: InquiryUI) => void;
  onEdit?: (inquiryData: InquiryUI) => void;
  onDelete?: (inquiryId: string) => void;
  editMode?: boolean;
  inquiryData?: InquiryUI | null;
  projectName: string;
  packages: OfferStandalonePackageDTO[];
  initialPackage?: OfferStandalonePackageDTO | null;
  offerDisplayId?: string;
  hasCollectifileBoard?: boolean;
}

const AddInquiryForm: React.FC<AddInquiryFormProps> = ({
  open,
  onClose,
  onAdd,
  onEdit,
  onDelete,
  editMode = false,
  inquiryData = null,
  projectName,
  packages,
  initialPackage = null,
  offerDisplayId,
  hasCollectifileBoard = false
}) => {
  const [formData, setFormData] = useState({
    packageId: '',
    contractorEmail: '',
    contractorPhone1: '',
    contractorPhone2: '',
    subject: '',
    inviteToCollectifileBoard: false
  });
  const [selectedPackage, setSelectedPackage] = useState<OfferStandalonePackageDTO | null>(null);
  const [loading, setLoading] = useState(false);

  // Nowe stany dla wyszukiwania kontrahentów
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState<GoogleContactSuggestion[]>([]);
  const [loadingEmailSuggestions, setLoadingEmailSuggestions] = useState(false);

  // Wypełnij formularz danymi zapytania, jeśli jest w trybie edycji lub jeśli podano initialPackage
  useEffect(() => {
    if (editMode && inquiryData) {
      const foundPackage = packages.find(pkg => pkg.package_id === inquiryData.packageId);
      setSelectedPackage(foundPackage || null);

      setFormData({
        packageId: inquiryData.packageId || '',
        contractorEmail: inquiryData.contractorEmail || '',
        contractorPhone1: inquiryData.contractorPhone1 || '',
        contractorPhone2: inquiryData.contractorPhone2 || '',
        subject: inquiryData.subject || '',
        inviteToCollectifileBoard: inquiryData.inviteToCollectifileBoard || false
      });
    } else if (initialPackage) {
      // Jeśli podano initialPackage, ustaw go jako wybrany pakiet
      setSelectedPackage(initialPackage);

      // Usunięcie pierwszych 8 znaków z nazwy projektu, jeśli zaczyna się od numeru projektu
      const shortProjectName = projectName.match(/^\d{3}-\d{3}\s(.+)$/)
        ? projectName.match(/^\d{3}-\d{3}\s(.+)$/)?.[1] || projectName
        : projectName;

      const generatedSubject = `${shortProjectName}: Zapytanie ${initialPackage.name}`;

      setFormData({
        packageId: initialPackage.package_id,
        contractorEmail: '',
        contractorPhone1: '',
        contractorPhone2: '',
        subject: generatedSubject,
        inviteToCollectifileBoard: false
      });
    } else {
      // Reset formularza w trybie dodawania
      setSelectedPackage(null);
      setFormData({
        packageId: '',
        contractorEmail: '',
        contractorPhone1: '',
        contractorPhone2: '',
        subject: '',
        inviteToCollectifileBoard: false
      });
    }
  }, [editMode, inquiryData, packages, open, initialPackage, projectName]);

  // Generowanie tematu na podstawie wybranego pakietu
  useEffect(() => {
    if (selectedPackage) {
      // Usunięcie pierwszych 8 znaków z nazwy projektu, jeśli zaczyna się od numeru projektu
      const shortProjectName = projectName.match(/^\d{3}-\d{3}\s(.+)$/)
        ? projectName.match(/^\d{3}-\d{3}\s(.+)$/)?.[1] || projectName
        : projectName;

      const generatedSubject = `${shortProjectName}: Zapytanie ${selectedPackage.name}`;
      setFormData(prev => ({
        ...prev,
        packageId: selectedPackage.package_id,
        subject: generatedSubject
      }));
    }
  }, [selectedPackage, projectName]);

  // Funkcja do wyszukiwania kontrahentów
  const searchContractors = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setLoadingContractors(true);
      const filters = { searchText: query };
      const response = await contractorService.filterContractors(filters, 1, 10);
      setSearchResults(response.contractors);
      setShowResults(true);
    } catch (error) {
      console.error('Błąd podczas wyszukiwania kontrahentów:', error);
      setSearchResults([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  // Obsługa wyboru kontrahenta z wyników wyszukiwania
  const handleSelectContractor = (contractor: Contractor) => {
    // Wybierz odpowiedni adres email
    let emailToUse = '';

    // Jeśli email jest "brak" lub "brak email" i istnieje email2, użyj email2
    if ((contractor.email === 'brak' || contractor.email === 'brak email' ||
         contractor.email === 'Brak email' || contractor.email === 'Brak') &&
        contractor.email2 && contractor.email2.trim() !== '') {
      emailToUse = contractor.email2;
    } else {
      // W przeciwnym razie użyj głównego adresu email
      emailToUse = contractor.email || '';
    }

    setFormData(prev => ({
      ...prev,
      contractorEmail: emailToUse
    }));

    setShowResults(false);
    setSearchQuery('');
  };

  const handlePackageChange = (event: React.SyntheticEvent, value: OfferStandalonePackageDTO | null) => {
    setSelectedPackage(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  useEffect(() => {
    const q = formData.contractorEmail?.trim() || '';
    if (q.length < 2) {
      setEmailSuggestions([]);
      return;
    }
    let active = true;
    setLoadingEmailSuggestions(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchGoogleContacts(q);
        if (active) setEmailSuggestions(data);
      } catch (err) {
        if (active) setEmailSuggestions([]);
      } finally {
        if (active) setLoadingEmailSuggestions(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [formData.contractorEmail]);

  const handleSubmit = () => {
    if (!selectedPackage) return;

    setLoading(true);

    // Przygotowanie danych zapytania
    const inquiryToSave: InquiryUI = {
      // W trybie edycji używamy istniejącego ID, w trybie dodawania używamy tymczasowego ID,
      // które zostanie zastąpione przez backend (format: Z.[sequence_number].[project_id].[package_id])
      id: editMode && inquiryData ? inquiryData.id : `TEMP-${Date.now().toString().slice(-6)}`,
      packageId: formData.packageId,
      packageName: selectedPackage.name,
      contractorEmail: formData.contractorEmail,
      contractorPhone1: formData.contractorPhone1 || undefined,
      contractorPhone2: formData.contractorPhone2 || undefined,
      subject: formData.subject,
      status: 'Przygotowane',
      createdAt: new Date().toISOString(),
      inviteToCollectifileBoard: formData.inviteToCollectifileBoard
    };

    console.log('Dane zapytania do zapisania:', inquiryToSave);

    try {
      if (editMode && onEdit) {
        onEdit(inquiryToSave);
      } else {
        onAdd(inquiryToSave);
      }
    } catch (error) {
      console.error('Błąd podczas zapisywania zapytania:', error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  // Style pól formularza zgodne z AddPackageForm
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {editMode ? 'Edytuj zapytanie' : `Dodaj nowe zapytanie${offerDisplayId ? ` w ${offerDisplayId}` : ''}`}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12}>
              <FieldLabel text="Wybierz pakiet" required />
              <Autocomplete
                options={packages}
                getOptionLabel={(option) => `${option.package_symbol} - ${option.name}`}
                value={selectedPackage}
                onChange={handlePackageChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Wybierz pakiet z listy"
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-input': {
                        ...textFieldSx['& .MuiInputBase-input'],
                        color: selectedPackage ? '#2D2D32' : '#B9B9B9'
                      }
                    }}
                  />
                )}
              />
            </Grid>

          <Grid item xs={12}>
            {/* Pole wyszukiwania kontrahenta */}
            <Box sx={{ mb: 2, position: 'relative' }}>
              <FieldLabel text="Wyszukaj kontrahenta" />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchContractors(e.target.value);
                }}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {loadingContractors ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Search />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Wyniki wyszukiwania */}
              {showResults && searchResults.length > 0 && (
                <Paper
                  elevation={3}
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    maxHeight: '200px',
                    overflow: 'auto',
                    zIndex: 1000,
                    mt: 0.5
                  }}
                >
                  <List dense>
                    {searchResults.map((contractor) => (
                      <React.Fragment key={contractor.id}>
                        <ListItem
                          button
                          onClick={() => handleSelectContractor(contractor)}
                        >
                          <ListItemText
                            primary={contractor.name}
                            secondary={
                              <>
                                <Typography variant="body2" component="span">
                                  {contractor.email === 'brak' || contractor.email === 'brak email' ||
                                   contractor.email === 'Brak email' || contractor.email === 'Brak' ? (
                                    contractor.email2 && contractor.email2.trim() !== '' ? (
                                      <>{contractor.email2} <span style={{ color: '#999' }}>(dodatkowy)</span></>
                                    ) : (
                                      <>brak email</>
                                    )
                                  ) : (
                                    <>
                                      {contractor.email}
                                      {contractor.email2 && contractor.email2.trim() !== '' && (
                                        <>, {contractor.email2} <span style={{ color: '#999' }}>(dodatkowy)</span></>
                                      )}
                                    </>
                                  )}
                                </Typography>
                                <br />
                                <Typography variant="body2" component="span" color="text.secondary">
                                  {contractor.location}, NIP: {contractor.nip}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>

            <FieldLabel text="Adres e-mail kontrahenta" required />
            <Autocomplete
              freeSolo
              options={emailSuggestions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.email}
              filterOptions={(x) => x}
              loading={loadingEmailSuggestions}
              inputValue={formData.contractorEmail}
              onChange={(event, value) => {
                if (typeof value === 'string') {
                  setFormData((prev) => ({ ...prev, contractorEmail: value }));
                } else if (value) {
                  setFormData((prev) => ({ ...prev, contractorEmail: value.email }));
                }
              }}
              onInputChange={(event, value) => {
                setFormData((prev) => ({ ...prev, contractorEmail: value }));
              }}
              isOptionEqualToValue={(opt, val) => {
                const o = typeof opt === 'string' ? opt : opt.email;
                const v = typeof val === 'string' ? val : val.email;
                return o === v;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  name="contractorEmail"
                  type="email"
                  variant="outlined"
                  size="small"
                  placeholder="kontrahent@example.com"
                  autoComplete="off"
                  sx={textFieldSx}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off',
                    spellCheck: false,
                    autoCorrect: 'off',
                    autoCapitalize: 'off'
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {loadingEmailSuggestions ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FieldLabel text="Nr telefonu 1" />
            <TextField
              fullWidth
              name="contractorPhone1"
              value={formData.contractorPhone1}
              onChange={handleChange}
              variant="outlined"
              size="small"
              placeholder="+48 123 456 789"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FieldLabel text="Nr telefonu 2" />
            <TextField
              fullWidth
              name="contractorPhone2"
              value={formData.contractorPhone2}
              onChange={handleChange}
              variant="outlined"
              size="small"
              placeholder="+48 123 456 789"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <FieldLabel text="Temat" required />
            <TextField
              fullWidth
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              variant="outlined"
              size="small"
              placeholder="Wpisz temat"
              sx={textFieldSx}
            />
          </Grid>

          {hasCollectifileBoard && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.inviteToCollectifileBoard}
                    onChange={(e) => setFormData(prev => ({ ...prev, inviteToCollectifileBoard: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Zaproś do boardu Collectifile"
                sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}
              />
            </Grid>
          )}
        </Grid>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Box>
            {editMode && onDelete && inquiryData && (
              <Button 
                onClick={() => {
                  if (inquiryData.id && window.confirm('Czy na pewno chcesz usunąć to zapytanie?')) {
                    onDelete(inquiryData.id);
                    onClose();
                  }
                }} 
                variant="outlined" 
                color="error"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  textTransform: 'none'
                }}
              >
                Usuń
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              variant="text"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                color: '#2D2D32',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!selectedPackage || !formData.contractorEmail}
              sx={{
                backgroundColor: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#030360'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              {editMode ? 'Zapisz zmiany' : 'Dodaj zapytanie'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddInquiryForm;
